import React, { useEffect, useState } from 'react';
import styles from './ccf-customer-card-history.module.scss';
import {
  TextField,
  Direction,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  useTheme,
} from '@mui/material';
import CcfCustomerHistoryCard from '../ccf-customer-history-card/ccf-customer-history-card';
import {
  contactHistoryToShow,
  loadContactHistoryData,
  activeContactSelector,
} from '../../ccf-inbox.slice';
import { useSelector, useDispatch } from 'react-redux';
import { CustomerHistory } from '../../model/customer-history';
import {
  CcfButton,
  CcfSearchIcon,
  CcfDoubleArrowIcon,
  CcfBox,
  CcfTypography,
  CcfAccordionSummary,
  CcfAccordion,
  CcfAccordionDetails,
  CcfTextField,
} from '@nice-ccf/ui-controls';

import { withTranslation, withTranslationProps } from '@nice-ccf/ui-controls';

import {
  CcfEditPenIcon,
  CcfFilterIcon,
  CcfInboundIcon,
  CcfOutboundIcon,
} from '@nice-ccf/ui-controls';
import { Contact } from '../../model/contact';
import { getApplicationDirection } from '@nice-ccf/ui-components';
import customerCardStyles from '../ccf-customer-card.styles';

/* eslint-disable-next-line */
export interface CcfCustomerContactHistoryProps {}

/**
 * CcfCustomerContactHistory - used to display Contact history accordion in Contact Card
 * @param props - CcfCustomerContactHistoryProps & withTranslationProps
 * @example - <CcfCustomerContactHistory>
 */
export function CcfCustomerContactHistory(
  props: CcfCustomerContactHistoryProps & withTranslationProps
) {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = React.useState('contacthistory');
  const historyData = useSelector(contactHistoryToShow) || [];
  const activeContact = useSelector(activeContactSelector) as Contact;
  const totalHistory = activeContact?.customer.customerHistory || [];
  const cardItemsPerPage = 2;
  const [next, setNext] = useState(2);
  const [isSearchEnable, setSearchEnable] = useState(false);
  const [selectedCategory, setSelectedCategory] = React.useState('all');
  const [serachInput, setSerachInput] = React.useState('');
  const appDirection: Direction = useSelector(getApplicationDirection);
  const theme = useTheme();
  const stylesAccordion = customerCardStyles(theme);
  const { translator } = props;

  useEffect(() => {
    setSerachInput('');
    setSelectedCategory('all');
    setNext(2);
  }, [activeContact?.id]);

  /**
   * It handled to fetch more posts as per request
   * @example - handleViewMoreHistory()
   */
  const handleViewMoreHistory = () => {
    dispatch(
      loadContactHistoryData({
        activeContact: activeContact,
        start: 0,
        next: totalHistory.length,
        filterByCategory: selectedCategory,
      })
    );
    setNext(next + cardItemsPerPage);
  };

  /**
   * It handled to view less posts after loaded all the posts
   * @example - handleViewLessHistory()
   */
  const handleViewLessHistory = () => {
    dispatch(
      loadContactHistoryData({
        activeContact: activeContact,
        start: 0,
        next: 2,
        filterByCategory: selectedCategory,
      })
    );
    setNext(cardItemsPerPage);
  };

  /**
   * handleChange Event - used to expand and collapse of address accordion
   * @param panel- 'contacthistory'
   * @param event - changeevent
   * @param newExpanded - boolean
   * @example - handleChange(panel)
   */
  const handleChange = (panel: string) => {
    setExpanded(expanded === 'contacthistory' ? '' : panel);
    setSearchEnable(false);
    handleViewLessHistory();
  };

  /**
   * Filter the history posts based on selected category
   * @param event - React.ChangeEvent name?: string | undefined; value: unknown;
   * @example - changeCategory(event)
   */
  const changeCategory = (
    event: React.ChangeEvent<{ name?: string | undefined; value: unknown }>
  ) => {
    dispatch(
      loadContactHistoryData({
        activeContact: activeContact,
        start: 0,
        next: 2,
        filterByCategory: (event.target as HTMLSelectElement).value,
      })
    );
    setSelectedCategory((event.target as HTMLSelectElement).value);
  };

  /**
   * Enable and Disable Search cateogry
   * @param event - React.SyntheticEvent
   * @example - searchEnable(event)
   */
  const searchEnable = (event?: React.SyntheticEvent) => {
    event?.stopPropagation();
    setSearchEnable(!isSearchEnable);
  };

  /**
   * Search the history posts based on selected category and search input
   * @param event - React.ChangeEvent
   * @example - onSearchInputChange(event)
   */
  const onSearchInputChange = (event: React.ChangeEvent) => {
    setSerachInput((event.target as HTMLInputElement).value);
    dispatch(
      loadContactHistoryData({
        activeContact: activeContact,
        start: 0,
        next: 2,
        filterByCategory: selectedCategory,
        searchByInput: (event.target as HTMLInputElement).value,
      })
    );
  };

  return (
    <section>
      <CcfAccordion
        square
        expanded={expanded === 'contacthistory'}
        onChange={() => handleChange('contacthistory')}
        sx={stylesAccordion.accordionContainer}
      >
        <CcfAccordionSummary
          expandIcon={<CcfDoubleArrowIcon sx={stylesAccordion.expandedIcon} />}
          aria-controls="contact-history-header"
          id="agent_appSpace_contactHistory"
          sx={
            expanded === 'contacthistory'
              ? stylesAccordion.accordionHeaderExpand
              : stylesAccordion.ccfAppSpaceAccordionHeader
          }
        >
          <CcfBox className={styles.contactHistoryInfo}>
            <CcfBox className={styles.titleRow}>
              <CcfTypography className={styles.title}>
                {translator('contactCardHistory')}
              </CcfTypography>
              {expanded === 'contacthistory' && (
                <IconButton className={styles.searchIcon} disableRipple>
                  {!isSearchEnable ? (
                    <CcfSearchIcon
                      onClick={searchEnable}
                      className={styles.searchIcon}
                      classes={{
                        root: styles.searchColor,
                      }}
                    />
                  ) : (
                    <CcfTypography
                      onClick={searchEnable}
                      className={styles.hideSearch}
                    >
                      {' '}
                      {translator('hide')}
                    </CcfTypography>
                  )}
                </IconButton>
              )}
            </CcfBox>

            <CcfBox
              className={
                expanded === 'contacthistory'
                  ? styles.titleDescription
                  : styles.collapsedTitleDescription
              }
            >
              <CcfTypography className={styles.entriesLabel}>
                {totalHistory && totalHistory.length} {translator('entries')}
              </CcfTypography>
              <IconButton className={styles.statusCount} disableRipple>
                <CcfInboundIcon />
                <CcfTypography
                  variant="subtitle1"
                  className={styles.historyCount}
                >
                  {activeContact && activeContact?.customer.ibHistoryCount}
                </CcfTypography>
              </IconButton>
              <IconButton className={styles.statusCount} disableRipple>
                <CcfOutboundIcon />
                <CcfTypography
                  variant="subtitle1"
                  className={styles.historyCount}
                >
                  {activeContact && activeContact?.customer.obHistoryCount}
                </CcfTypography>
              </IconButton>
              <IconButton className={styles.statusCount} disableRipple>
                <CcfEditPenIcon />
                <CcfTypography
                  variant="subtitle1"
                  className={styles.historyCount}
                >
                  {activeContact && activeContact?.customer.otherHistoryCount}
                </CcfTypography>
              </IconButton>
            </CcfBox>
          </CcfBox>
        </CcfAccordionSummary>
        <CcfAccordionDetails>
          {isSearchEnable && (
            <CcfBox component="div" className={styles.searchContainer}>
              <CcfBox className={styles.searchBoxSection}>
                <CcfBox className={styles.searchBox}>
                  <CcfTextField
                    id="search"
                    placeholder={translator('search')}
                    size="small"
                    classes={{
                      root: styles.searchInput,
                    }}
                    type="text"
                    value={serachInput}
                    onChange={onSearchInputChange}
                    InputProps={{
                      disableUnderline: true,
                      startAdornment: (
                        <InputAdornment position="start">
                          <CcfSearchIcon
                            classes={{
                              root: styles.searchIconColor,
                            }}
                          ></CcfSearchIcon>
                        </InputAdornment>
                      ),
                    }}
                  />
                </CcfBox>
                <CcfBox>
                  <CcfButton
                    variant="outlined"
                    classes={{
                      root: styles.filterBtn,
                    }}
                  >
                    <CcfFilterIcon />
                  </CcfButton>
                </CcfBox>
              </CcfBox>
              <CcfBox className={styles.categorySelectDropdown}>
                <Select
                  displayEmpty
                  value={selectedCategory}
                  onChange={(e: any) => changeCategory(e)}
                  sx={stylesAccordion.selectDropdown}
                  disableUnderline
                >
                  <MenuItem value={'all'}> {translator('all')}</MenuItem>
                  <MenuItem value={'inbound'}>
                    {' '}
                    {translator('inbound')}
                  </MenuItem>
                  <MenuItem value={'outbound'}>
                    {' '}
                    {translator('outbound')}
                  </MenuItem>
                  <MenuItem value={'other'}> {translator('addEdit')}</MenuItem>
                </Select>
              </CcfBox>
            </CcfBox>
          )}

          <CcfBox component="div" className={styles.historyDetails}>
            {historyData &&
              historyData.length !== 0 &&
              historyData.map((cardItem: CustomerHistory, index: number) => (
                <CcfCustomerHistoryCard
                  key={`customer-history-card-${index}`}
                  interactionId={activeContact.id}
                  cardInfo={cardItem}
                  index={index}
                />
              ))}
          </CcfBox>
        </CcfAccordionDetails>
        {activeContact &&
          activeContact?.customer.filteredHistoryCount !== 0 &&
          historyData.length < totalHistory.length && (
            <CcfButton
              color="primary"
              disableRipple={true}
              classes={{
                root: styles.viewMorebtn,
              }}
              onClick={handleViewMoreHistory}
            >
              <CcfTypography
                translationKey="viewMore"
                extraArgs={{
                  format: [totalHistory.length - historyData.length],
                }}
                variant="button"
                className={styles.viewMorebtn}
              ></CcfTypography>
            </CcfButton>
          )}
        {activeContact &&
          activeContact?.customer.filteredHistoryCount !== 0 &&
          historyData.length === totalHistory.length && (
            <CcfButton
              color="primary"
              disableRipple={true}
              classes={{
                root: styles.viewMorebtn,
              }}
              onClick={handleViewLessHistory}
            >
              {translator('viewLess')}
            </CcfButton>
          )}
      </CcfAccordion>
      <CcfBox component="div" className={styles.bottomBorder}></CcfBox>
    </section>
  );
}

export default withTranslation(CcfCustomerContactHistory);
